import { TlgRealtimeDto } from '@/lib/api/data-contracts';
import { Ruler, Thermometer } from 'lucide-react';
import { useEffect, useRef } from 'react';

interface TankGaugeProps {
  tankData: TlgRealtimeDto;
}

export function TankGauge({ tankData }: TankGaugeProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Calculate percentage
  const percentage =
    tankData.tkTotal && tankData.liquidQty
      ? Math.floor((tankData.liquidQty / tankData.tkTotal) * 100)
      : 0;

  // Oil color mapping
  const getOilColor = (oilCode: string | undefined): string => {
    const colorMap: Record<string, string> = {
      '1001': '#FF0000', // 휘발유 - 빨간색
      '1002': '#FFFFFF', // 경유 - 흰색
      '1003': '#FFFF00', // 등유 - 노란색
      '1004': '#80FF00', // 중유 - 연두색
      '1005': '#F4C473', // LPG - 주황색
      '1006': '#F4C473', // 기타 - 주황색
    };
    return colorMap[oilCode || ''] || '#4CAF50';
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    const radius = Math.min(width, height) / 2 - 10;
    const centerX = width / 2;
    const centerY = height / 2;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Draw outer circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.strokeStyle = '#ddd';
    ctx.lineWidth = 3;
    ctx.stroke();

    // Draw liquid fill
    if (percentage > 0) {
      const fillHeight = (radius * 2 * percentage) / 100;
      const fillY = centerY + radius - fillHeight;

      ctx.beginPath();
      ctx.arc(centerX, centerY, radius - 2, 0, 2 * Math.PI);
      ctx.clip();

      ctx.fillStyle = getOilColor(tankData.oilCode);
      ctx.fillRect(centerX - radius, fillY, radius * 2, fillHeight);
    }

    // Draw percentage text
    ctx.fillStyle = '#333';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(`${percentage}%`, centerX, centerY + 5);
  }, [percentage, tankData.oilCode]);

  return (
    <div className='bg-gray-50 rounded-lg p-4 text-center'>
      <canvas
        ref={canvasRef}
        width={120}
        height={120}
        className='mx-auto mb-2'
      />
      <div className='text-sm space-y-1'>
        <div className='font-semibold'>Tank No. {tankData.tankNo}</div>
        <div className='text-gray-600'>
          {tankData.liquidQty?.toLocaleString()} /{' '}
          {tankData.tkTotal?.toLocaleString()}L
        </div>
        <div className='flex items-center justify-center gap-1 text-xs text-gray-500'>
          <Thermometer className='w-3 h-3' />
          {tankData.sensorTemp}°C
        </div>
        <div className='flex items-center justify-center gap-1 text-xs text-gray-500'>
          <Ruler className='w-3 h-3' />
          {tankData.sensorHei}mm
        </div>
      </div>
    </div>
  );
}
